import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';
import { ArrowRightIcon } from 'lucide-react';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore utils is in the parent dir
import isProd from '@utils/isProd';

import { AddDateDetailsButton } from 'components/AddDateDetailsButton';
import { ProcessedAttachment } from 'components/AttachmentUpload';
import { Button } from 'components/Button';
import { DatePicker } from 'components/DatePicker';
import { Label } from 'components/Label';
import { Input } from 'components/input/Input';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { ExtendedFormProvider } from 'contexts/extendedFormContext';
import { CarrierQuoteConfig } from 'contexts/serviceContext';
import { isCustomerSupportedTMS } from 'hooks/useCustomers';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { useToast } from 'hooks/useToaster';
import {
  NewAttachment,
  emailCarriersForQuotes,
} from 'lib/api/emailCarriersForQuotes';
import fetchEmailTemplates from 'lib/api/fetchEmailTemplates';
import { getCustomers } from 'lib/api/getCustomers';
import { getQuoteRequest } from 'lib/api/getQuoteRequest';
import { CarrierGroup } from 'types/CarrierGroup';
import { Email } from 'types/Email';
import { EmailTemplate, TemplateType } from 'types/EmailTemplates';
import { TMSCustomer, TMSLocationWithDistance } from 'types/Load';
import {
  Address,
  QuoteRequest,
  Address as QuoteRequestAddress,
} from 'types/QuoteRequest';
import { Maybe } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import captureException from 'utils/captureException';
import getEmailAddress from 'utils/getEmailAddress';
import { nl2br } from 'utils/getJoditEditorHelpers';
import {
  GenericCompanySearchableFields,
  customerSearchHandler,
} from 'utils/loadInfoAndBuilding';

import FindCarrierByGroup from './FindCarrierByGroup';
import LoadInformationForm from './LoadInformationForm';
import MessageCarrierForm from './MessageCarrierForm';
import {
  buildCQEmailTemplatesQuery,
  formatCQInlineVariable,
} from './emailTemplate';
import {
  handleEmailCarrierForQuoteResponse,
  parseLocationString,
} from './helperFunctions';
import { CarrierFormConfig, CarrierQuoteInputs } from './types';

function parseCcEmails(cc: CarrierQuoteInputs['cc']): string[] {
  if (Array.isArray(cc)) {
    return cc;
  }

  if (typeof cc !== 'string') {
    return [];
  }

  return cc
    .split(',')
    .map((email) => email.trim())
    .filter((email) => email !== '');
}

interface RequestCarrierQuoteByGroupProps {
  email: Maybe<Email>;
  quoteRequest: Maybe<QuoteRequest>;
  setQuoteRequest: React.Dispatch<React.SetStateAction<Maybe<QuoteRequest>>>;
  // QuoteRequest may be null if there's a transient network error getting the parsed values. In those cases, still show the form
  carrierQuoteConfig: CarrierQuoteConfig;
}

export default function RequestCarrierQuotesByGroup({
  email,
  quoteRequest,
  setQuoteRequest,
  carrierQuoteConfig,
}: RequestCarrierQuoteByGroupProps) {
  const { toast } = useToast();
  const [customers, setCustomers] = useState<Maybe<TMSCustomer[]>>(null);
  const [initialCustomers, setInitialCustomers] =
    useState<Maybe<TMSCustomer[]>>(null);
  const [selectedCarrierGroup, setSelectedCarrierGroup] =
    useState<Maybe<CarrierGroup>>(null);

  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);

  const [loading, setLoading] = useState(false);
  const [carrierLocations, setCarrierLocations] = useState<
    TMSLocationWithDistance[]
  >([]);
  const [showMessageCarrier, setShowMessageCarrier] = useState(false);
  const [selectedCarriers, setSelectedCarriers] = useState<
    Record<string, boolean>
  >({});
  const [newAttachments, setNewAttachments] = useState<ProcessedAttachment[]>(
    []
  );
  const [areAllCarriersSelected, setAreAllCarriersSelected] =
    useState<boolean>(true);
  const { serviceID, tmsIntegrations } = useServiceFeatures();
  const [initialFromEmail, setInitialFromEmail] = useState<Maybe<string>>(
    carrierQuoteConfig.from ?? null
  );
  const [hasAddedQuoteDates, setHasAddedQuoteDates] = useState<boolean>(false);

  useEffect(() => {
    if (carrierLocations.length === 0) {
      setAreAllCarriersSelected(false);
    } else {
      const allSelected = carrierLocations.every(
        (carrier) =>
          carrier.externalTMSID && selectedCarriers[carrier.externalTMSID]
      );
      setAreAllCarriersSelected(allSelected);
    }
  }, [carrierLocations, selectedCarriers]);

  if (tmsIntegrations?.length > 0) {
    if (tmsIntegrations.length > 1 && isProd()) {
      captureException(
        new Error(
          'Service with multiple TMS integrations trying to find carriers'
        ),
        { serviceID: serviceID, tmsIntegrations: tmsIntegrations }
      );
    }
  }

  const config: CarrierFormConfig = useMemo(() => {
    return {
      from: initialFromEmail || carrierQuoteConfig.from || '',
      cc: carrierQuoteConfig.cc ?? [],
      bcc: carrierQuoteConfig.bcc ?? [],
      bccCarriers: carrierQuoteConfig.bccCarriers,
      subject: carrierQuoteConfig.subject ?? '',
      emailBody: carrierQuoteConfig.emailBody ?? '',
      showCustomerSearch: carrierQuoteConfig.showCustomerSearch,
      showItemDescription: carrierQuoteConfig.showItemDescription,
      showDeliverySection: carrierQuoteConfig.showDeliverySection,
      requireDeliveryLocation: carrierQuoteConfig.requireDeliveryLocation,
      showPickupAddressLine1: carrierQuoteConfig.showPickupAddressLine1,
      showDeliveryAddressLine1: carrierQuoteConfig.showDeliveryAddressLine1,
      isFindCarrierByGroupEnabled: true,
      isFindCarrierByLocationEnabled:
        carrierQuoteConfig.isFindCarrierByLocationEnabled,
    };
  }, [initialFromEmail, carrierQuoteConfig]);

  const carrierProcessingFormMethods = useForm<CarrierQuoteInputs>({
    mode: 'onChange',
    resolver: async (values) => {
      const errors: Record<string, any> = {};

      // Validate pickup location
      if (!values.pickupLocation?.location) {
        errors.pickupLocation = {
          location: {
            type: 'required',
            message: 'Pickup location is required',
          },
        };
      }

      // Validate mile radius only if location search is enabled and group search is not solely active
      if (
        config.isFindCarrierByLocationEnabled &&
        !config.isFindCarrierByGroupEnabled &&
        !values.mileRadius
      ) {
        errors.mileRadius = {
          type: 'required',
          message: 'Search radius is required',
        };
      } else if (
        config.isFindCarrierByLocationEnabled &&
        !config.isFindCarrierByGroupEnabled &&
        values.mileRadius &&
        values.mileRadius < 1
      ) {
        errors.mileRadius = {
          type: 'min',
          message: 'Search radius must be at least 1 mile',
        };
      }

      if (
        config.requireDeliveryLocation &&
        !values.deliveryLocation?.location
      ) {
        errors.deliveryLocation = {
          location: {
            type: 'required',
            message: 'Delivery location is required',
          },
        };
      }

      return {
        values: values as any, // Preserve user input for correction
        errors: Object.keys(errors).length > 0 ? errors : {},
      };
    },
  });

  const messageCarrierFormMethods = useForm<CarrierQuoteInputs>({
    mode: 'onChange',
  });

  useEffect(() => {
    if (
      tmsIntegrations?.length > 0 &&
      isCustomerSupportedTMS(tmsIntegrations) &&
      config.showCustomerSearch
    ) {
      const fetchCustomers = async () => {
        const res = await getCustomers(tmsIntegrations[0].id);
        if (res.isOk()) {
          setInitialCustomers(res.value.customerList);
          setCustomers(res.value.customerList);
        }
      };
      fetchCustomers();
    }

    if (!initialFromEmail) {
      const fetchUserEmail = async () => {
        const userEmail = await getEmailAddress();
        if (userEmail) {
          setInitialFromEmail(userEmail);
          // Update form defaults after fetching email
          carrierProcessingFormMethods.reset({
            ...carrierProcessingFormMethods.getValues(),
            from: userEmail,
          });
          messageCarrierFormMethods.reset({
            ...messageCarrierFormMethods.getValues(),
            from: userEmail,
          });
        }
      };
      fetchUserEmail();
    }
  }, [
    tmsIntegrations,
    initialFromEmail,
    carrierProcessingFormMethods,
    messageCarrierFormMethods,
    config.showCustomerSearch,
  ]);

  const defaultValues = useMemo<CarrierQuoteInputs>(() => {
    const baseValues: CarrierQuoteInputs = {
      // Load details
      customerId: null,
      itemDescription: '',
      commodity: null,
      transportType: null,
      weightLbs: 0,
      weightUnit: 'lbs',

      // Pickup details
      pickupLocation: {
        name: '',
        addressLine1: '',
        addressLine2: '',
        zip: '',
        city: '',
        state: '',
        country: 'USA',
        location: '',
      },
      pickupStartDate: null,
      pickupEndDate: null,
      mileRadius: 50,

      // Delivery details
      deliveryLocation: {
        name: '',
        addressLine1: '',
        addressLine2: '',
        zip: '',
        city: '',
        state: '',
        country: 'USA',
        location: '',
      },
      deliveryStartDate: null,
      deliveryEndDate: null,

      // Email details
      from: config.from,
      cc: config.cc,
      bcc: config.bcc,
      subject: config.subject,
      emailBody: nl2br(config.emailBody), // Replace newlines with <br> tags

      // Carrier details
      carriers: [],
      carrierEmails: [],
      selectedExistingAttachments: [],

      carrierGroupId: undefined,
    };

    if (quoteRequest) {
      return {
        ...baseValues,
        transportType: quoteRequest.transportType || baseValues.transportType,
        commodity: quoteRequest.commodity || baseValues.commodity,
        weightLbs: quoteRequest.weightLbs || baseValues.weightLbs,
        weightUnit: quoteRequest.weightUnit || baseValues.weightUnit,
        pickupStartDate:
          quoteRequest.pickupStartDate || baseValues.pickupStartDate,
        pickupEndDate: quoteRequest.pickupEndDate || baseValues.pickupEndDate,
        deliveryStartDate:
          quoteRequest.deliveryStartDate || baseValues.deliveryStartDate,
        deliveryEndDate:
          quoteRequest.deliveryEndDate || baseValues.deliveryEndDate,
        pickupLocation: {
          name: quoteRequest.pickupLocation?.name || '',
          addressLine1: quoteRequest.pickupLocation?.addressLine1 || '',
          addressLine2: quoteRequest.pickupLocation?.addressLine2 || '',
          zip: quoteRequest.pickupLocation?.zip || '',
          city: quoteRequest.pickupLocation?.city || '',
          state: quoteRequest.pickupLocation?.state || '',
          country: quoteRequest.pickupLocation?.country || 'USA',
          location:
            quoteRequest.pickupLocation?.zip ||
            (quoteRequest.pickupLocation?.city &&
            quoteRequest.pickupLocation?.state
              ? `${quoteRequest.pickupLocation.city}, ${quoteRequest.pickupLocation.state}`
              : ''),
        },
        deliveryLocation: {
          name: quoteRequest.deliveryLocation?.name || '',
          addressLine1: quoteRequest.deliveryLocation?.addressLine1 || '',
          addressLine2: quoteRequest.deliveryLocation?.addressLine2 || '',
          zip: quoteRequest.deliveryLocation?.zip || '',
          city: quoteRequest.deliveryLocation?.city || '',
          state: quoteRequest.deliveryLocation?.state || '',
          country: quoteRequest.deliveryLocation?.country || 'USA',
          location:
            quoteRequest.deliveryLocation?.zip ||
            (quoteRequest.deliveryLocation?.city &&
            quoteRequest.deliveryLocation?.state
              ? `${quoteRequest.deliveryLocation.city}, ${quoteRequest.deliveryLocation.state}`
              : ''),
        },
        carriers: [],
      };
    }

    return baseValues;
  }, [quoteRequest, config]);

  useEffect(() => {
    carrierProcessingFormMethods.reset(defaultValues);
    messageCarrierFormMethods.reset(defaultValues);
  }, [defaultValues, carrierProcessingFormMethods, messageCarrierFormMethods]);

  const processSelectedGroup: SubmitHandler<CarrierQuoteInputs> = async (
    data
  ) => {
    setLoading(true);
    setSelectedCarriers({});
    messageCarrierFormMethods.reset();
    setCarrierLocations([]);
    setShowMessageCarrier(false);

    if (data.customerId) {
      messageCarrierFormMethods.setValue('customerId', data.customerId);
    }

    if (
      !selectedCarrierGroup ||
      !selectedCarrierGroup.carriers ||
      selectedCarrierGroup.carriers.length === 0
    ) {
      setLoading(false);
      return;
    }

    const groupCarriers = selectedCarrierGroup.carriers;
    const carrierEmails = groupCarriers
      .map((c) => c.email)
      .filter(Boolean) as string[];
    messageCarrierFormMethods.setValue('carrierEmails', carrierEmails);

    const carrierProcessingData = carrierProcessingFormMethods.getValues();

    // Parse locations
    const parsedPickupLocation = parseLocationString(
      carrierProcessingData.pickupLocation.location
    );
    const finalPickupLocation: QuoteRequestAddress & {
      location: Maybe<string>;
    } = {
      name: carrierProcessingData.pickupLocation.name || '',
      addressLine1: carrierProcessingData.pickupLocation.addressLine1 || '',
      addressLine2: carrierProcessingData.pickupLocation.addressLine2 || '',
      city: parsedPickupLocation.city || '',
      state: parsedPickupLocation.state || '',
      zip: parsedPickupLocation.zip || '',
      country: carrierProcessingData.pickupLocation.country || 'USA',
      location: carrierProcessingData.pickupLocation.location,
    };

    let finalDeliveryLocation: Maybe<
      QuoteRequestAddress & { location: Maybe<string> }
    > = null;
    if (
      carrierProcessingData.deliveryLocation?.location ||
      carrierQuoteConfig.requireDeliveryLocation
    ) {
      const parsedDeliveryLocation = parseLocationString(
        carrierProcessingData.deliveryLocation?.location ?? null
      );
      finalDeliveryLocation = {
        name: carrierProcessingData.deliveryLocation?.name || '',
        addressLine1:
          carrierProcessingData.deliveryLocation?.addressLine1 || '',
        addressLine2:
          carrierProcessingData.deliveryLocation?.addressLine2 || '',
        city: parsedDeliveryLocation.city || '',
        state: parsedDeliveryLocation.state || '',
        zip: parsedDeliveryLocation.zip || '',
        country: carrierProcessingData.deliveryLocation?.country || 'USA',
        location: carrierProcessingData.deliveryLocation?.location ?? null,
      };
    }

    messageCarrierFormMethods.setValue(
      'itemDescription',
      carrierProcessingData.itemDescription
    );
    messageCarrierFormMethods.setValue(
      'commodity',
      carrierProcessingData.commodity
    );
    messageCarrierFormMethods.setValue(
      'transportType',
      carrierProcessingData.transportType
    );
    messageCarrierFormMethods.setValue(
      'weightLbs',
      carrierProcessingData.weightLbs
    );
    messageCarrierFormMethods.setValue(
      'weightUnit',
      carrierProcessingData.weightUnit
    );
    messageCarrierFormMethods.setValue('pickupLocation', finalPickupLocation);
    messageCarrierFormMethods.setValue(
      'pickupStartDate',
      carrierProcessingData.pickupStartDate
    );
    messageCarrierFormMethods.setValue(
      'pickupEndDate',
      carrierProcessingData.pickupEndDate
    );
    messageCarrierFormMethods.setValue(
      'deliveryLocation',
      finalDeliveryLocation || {
        name: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zip: '',
        country: 'USA',
        location: null,
      }
    );
    messageCarrierFormMethods.setValue(
      'deliveryStartDate',
      carrierProcessingData.deliveryStartDate
    );
    messageCarrierFormMethods.setValue(
      'deliveryEndDate',
      carrierProcessingData.deliveryEndDate
    );
    messageCarrierFormMethods.setValue(
      'selectedExistingAttachments',
      carrierProcessingData.selectedExistingAttachments
    );

    const carrierQuoteEmailTemplateQuery = buildCQEmailTemplatesQuery({
      carrierFormConfig: config,
      carrierProcessingData: {
        ...carrierProcessingData,
        pickupLocation: finalPickupLocation,
        deliveryLocation: finalDeliveryLocation ?? defaultDeliveryLocation,
        itemDescription: formatCQInlineVariable(
          carrierProcessingData.itemDescription
        ),
      },
    });

    const emailTemplatesResponse =
      await fetchEmailTemplates<TemplateType.CARRIER_QUOTE_BY_GROUP>({
        templateType: TemplateType.CARRIER_QUOTE_BY_GROUP,
        carrierQuoteQuery: carrierQuoteEmailTemplateQuery,
      });
    if (emailTemplatesResponse.isOk()) {
      setEmailTemplates(
        emailTemplatesResponse.value.carrierQuoteEmailTemplates
      );
    }

    messageCarrierFormMethods.setValue('from', config.from);
    messageCarrierFormMethods.setValue('cc', config.cc);
    messageCarrierFormMethods.setValue(
      'bcc',
      config.bccCarriers ? [...config.bcc, ...carrierEmails] : config.bcc
    );

    const initialSelected: Record<string, boolean> = {};
    carrierEmails.forEach((email) => {
      initialSelected[email] = true;
    });
    setSelectedCarriers(initialSelected);

    setCarrierLocations(
      groupCarriers.map(
        (gc) =>
          ({
            ...gc,
            ID: gc.ID,
            emails: gc.email ? [gc.email] : [],
            apptRequired: null,
            isShipper: undefined,
            isConsignee: undefined,
            driverLoadingResponsibility: undefined,
            carrier: gc,
            notes: undefined,
            milesDistance: undefined,
          }) as TMSLocationWithDistance
      )
    );
    setShowMessageCarrier(true);
    setLoading(false);
    toast({
      description: `Drafted email`,
      variant: 'success',
      duration: 1000,
    });
  };

  const handleGroupSelect = useCallback(
    (group: Maybe<CarrierGroup>) => {
      setSelectedCarrierGroup(group);
      setShowMessageCarrier(false);
      setCarrierLocations([]);
      if (group && group.carriers && group.carriers.length > 0) {
        carrierProcessingFormMethods.setValue(
          'carrierGroupId',
          group.id.toString()
        );
      } else if (group) {
        carrierProcessingFormMethods.setValue(
          'carrierGroupId',
          group.id.toString()
        );
      } else {
        carrierProcessingFormMethods.setValue('carrierGroupId', undefined);
      }
    },
    [
      setSelectedCarrierGroup,
      setShowMessageCarrier,
      setCarrierLocations,
      carrierProcessingFormMethods,
    ]
  );

  const toggleSelection = (carrierId: string) => {
    setSelectedCarriers((prevSelectedCarriers) => {
      const newSelectedCarriers = {
        ...prevSelectedCarriers,
        [carrierId]: !prevSelectedCarriers[carrierId],
      };

      const selectedEmailsArray = carrierLocations
        .filter(
          (carrier) =>
            carrier.externalTMSID && newSelectedCarriers[carrier.externalTMSID]
        )
        .flatMap(
          (carrier) => carrier.emails || (carrier.email ? [carrier.email] : [])
        )
        .filter((email): email is string => !!email)
        .map((email) => email.trim());
      messageCarrierFormMethods.setValue(
        'carrierEmails',
        Array.from(new Set(selectedEmailsArray))
      );

      // Update areAllCarriersSelected state
      if (carrierLocations.length > 0) {
        const allCurrentlySelected = carrierLocations.every(
          (loc) => loc.externalTMSID && newSelectedCarriers[loc.externalTMSID]
        );
        setAreAllCarriersSelected(allCurrentlySelected);
      } else {
        setAreAllCarriersSelected(false);
      }
      return newSelectedCarriers;
    });
  };

  const handleToggleSelectAllCarriers = () => {
    const newSelectedState = !areAllCarriersSelected;
    const newSelectedCarriers: Record<string, boolean> = {};
    const allCarrierEmailsForForm: string[] = [];

    carrierLocations.forEach((carrier) => {
      if (carrier.externalTMSID) {
        // Ensure externalTMSID exists
        newSelectedCarriers[carrier.externalTMSID] = newSelectedState;
        if (newSelectedState) {
          const emails =
            carrier.emails || (carrier.email ? [carrier.email] : []);
          emails.forEach((email) => {
            if (email) allCarrierEmailsForForm.push(email.trim());
          });
        }
      }
    });

    setSelectedCarriers(newSelectedCarriers);
    messageCarrierFormMethods.setValue(
      'carrierEmails',
      Array.from(new Set(allCarrierEmailsForForm))
    );
    // setAreAllCarriersSelected will be updated by the useEffect watching selectedCarriers
  };

  const onSubmitMessageCarriers: SubmitHandler<CarrierQuoteInputs> = async (
    data
  ) => {
    setLoading(true);

    const formattedAttachments: NewAttachment[] = newAttachments.map(
      (attachment) => ({
        data: attachment.data,
        fileName: attachment.fileName,
        mimeType: attachment.mimeType,
      })
    );

    const processedCcEmails = parseCcEmails(data.cc);

    const allGroupCarriers: TMSLocationWithDistance[] =
      selectedCarrierGroup?.carriers?.map(
        (gc) =>
          ({
            ...gc,
            emails: gc.email ? [gc.email] : [],
            apptRequired: null,
            isShipper: undefined,
            isConsignee: undefined,
            driverLoadingResponsibility: undefined,
            carrier: gc,
            notes: undefined,
            milesDistance: undefined,
          }) as TMSLocationWithDistance
      ) ?? [];

    // Filter carriers to only include those that are actually selected by the user
    // This ensures we only email the specific carriers/locations selected, not all carriers
    // that happen to share the same email address
    const payloadCarriers = allGroupCarriers.filter((carrier) => {
      return (
        carrier.externalTMSID !== undefined &&
        carrier.externalTMSID !== null &&
        selectedCarriers[carrier.externalTMSID] === true
      );
    });

    const response = await emailCarriersForQuotes(
      {
        ...data,
        weightLbs: data.weightLbs ? parseFloat(data.weightLbs.toString()) : 0,
        id: quoteRequest?.id ?? 0,
        threadID: email?.threadID ?? '',
        emailID: email?.id ?? 0,
        pallets: quoteRequest?.pallets ?? [],
        carrierNetworkEmails: null,
        carriers: payloadCarriers,
        carrierGroupId: selectedCarrierGroup?.id ?? undefined,
      },
      data.carrierEmails,
      processedCcEmails,
      data.subject,
      data.emailBody,
      data.selectedExistingAttachments,
      formattedAttachments
    );

    handleEmailCarrierForQuoteResponse(response, setLoading);

    // Refresh the Quote Request so the UI immediately shows the "Review Responses" section
    if (response.isOk() && email?.threadID) {
      const res = await getQuoteRequest(email.threadID);
      setQuoteRequest(res.isOk() ? res.value : null);
    }

    setLoading(false);
  };

  // Handler functions for customer search, moved inside the component body
  const handleRefreshCustomers = async () => {
    const tmsId = tmsIntegrations?.[0]?.id;
    if (!tmsId) return;
    const res = await getCustomers(tmsId, true);
    if (res.isOk()) {
      setInitialCustomers(res.value.customerList);
      setCustomers(res.value.customerList);
      toast({
        description: 'Successfully refreshed customer list.',
        variant: 'success',
      });
    } else {
      toast({
        description: 'Error while refreshing customer list.',
        variant: 'destructive',
      });
    }
  };

  const handleResetCustomerSearch = () => {
    setCustomers(initialCustomers);
  };

  const handleCustomerSearch = async (
    field: GenericCompanySearchableFields,
    value: string
  ): Promise<any[]> => {
    const tmsId = tmsIntegrations?.[0]?.id;
    if (!tmsId) {
      return [];
    }
    return customerSearchHandler({
      tmsID: tmsId,
      customers,
      setCustomers,
      field,
      value,
    });
  };

  return (
    <Grid gap='md' className='mx-0 w-full'>
      <ExtendedFormProvider aiDefaultValues={true}>
        <FormProvider {...carrierProcessingFormMethods}>
          <form
            onSubmit={carrierProcessingFormMethods.handleSubmit(
              processSelectedGroup
            )}
          >
            <Flex direction='col' gap='md' className='mx-0 w-full'>
              <LoadInformationForm
                config={config}
                customers={customers}
                handleRefreshCustomers={handleRefreshCustomers}
                handleResetCustomerSearch={handleResetCustomerSearch}
                handleCustomerSearch={handleCustomerSearch}
                tmsIntegrations={tmsIntegrations}
              />

              <Flex direction='col' gap='xs' className='mx-0 w-full'>
                {/* Pickup Location */}
                {config.showPickupAddressLine1 && (
                  <Flex direction='col' className='mb-2 w-full'>
                    <Label name='pickupLocation.addressLine1' required={true}>
                      Pickup Address Line 1
                    </Label>
                    <Input
                      {...carrierProcessingFormMethods.register(
                        'pickupLocation.addressLine1',
                        {
                          required: 'Address Line 1 is required',
                        }
                      )}
                      placeholder='e.g. 123 Main St'
                      className='mt-1'
                    />
                    <ErrorMessage
                      errors={carrierProcessingFormMethods.formState.errors}
                      name='pickupLocation.addressLine1'
                      render={({ message }: { message: string }) => (
                        <Typography
                          variant='body-xs'
                          className='text-error-500 mt-1'
                        >
                          {message}
                        </Typography>
                      )}
                    />
                  </Flex>
                )}
                {!config.showPickupAddressLine1 &&
                config.showDeliverySection ? (
                  <Flex
                    justify='between'
                    align='start'
                    gap='sm'
                    className='w-full mx-0'
                  >
                    <div className='w-full'>
                      <LocationInput
                        name='pickupLocation.location'
                        label='Pickup'
                        placeholder='City, State or ZIP'
                        required={true}
                      />
                    </div>
                    <ArrowRightIcon className='w-8 text-neutral-500 mt-7' />
                    <div className='w-full'>
                      <LocationInput
                        name='deliveryLocation.location'
                        label='Dropoff'
                        placeholder='City, State or ZIP'
                        required={config.requireDeliveryLocation}
                      />
                    </div>
                  </Flex>
                ) : (
                  <>
                    <LocationInput
                      name='pickupLocation.location'
                      label='Pickup City, State or Zip'
                      placeholder='e.g. 02116 or Boston, MA'
                      required={true}
                    />
                    {config.showDeliverySection && (
                      <div className='mt-2 w-full'>
                        {config.showDeliveryAddressLine1 && (
                          <Flex direction='col' className='mb-2 w-full'>
                            <Label
                              name='deliveryLocation.addressLine1'
                              required={true}
                            >
                              Delivery Address Line 1
                            </Label>
                            <Input
                              {...carrierProcessingFormMethods.register(
                                'deliveryLocation.addressLine1',
                                {
                                  required: 'Address Line 1 is required',
                                }
                              )}
                              placeholder='e.g. 123 Main St'
                              className='mt-1'
                            />
                            <ErrorMessage
                              errors={
                                carrierProcessingFormMethods.formState.errors
                              }
                              name='deliveryLocation.addressLine1'
                              render={({ message }: { message: string }) => (
                                <Typography
                                  variant='body-xs'
                                  className='text-error-500 mt-1'
                                >
                                  {message}
                                </Typography>
                              )}
                            />
                          </Flex>
                        )}
                        <LocationInput
                          name='deliveryLocation.location'
                          label='Delivery City, State or Zip'
                          placeholder='e.g. 02116 or Boston, MA'
                          required={config.requireDeliveryLocation}
                        />
                      </div>
                    )}
                  </>
                )}
              </Flex>

              {hasAddedQuoteDates && (
                <Grid cols='2' gap='lg' className='w-full mx-0'>
                  {/* Pickup Date */}
                  <div>
                    <Label name='pickupDates' className='mb-1'>
                      Pickup Date
                    </Label>
                    <Controller
                      name={'pickupStartDate'}
                      control={carrierProcessingFormMethods.control}
                      render={({ field }) => {
                        return <DatePicker field={field} />;
                      }}
                    />
                  </div>

                  {/* Delivery Date */}
                  {config.showDeliverySection && (
                    <div>
                      <Label name='deliveryDates' className='mb-1'>
                        Delivery Date
                      </Label>
                      <Controller
                        name={'deliveryStartDate'}
                        control={carrierProcessingFormMethods.control}
                        render={({ field }) => {
                          return <DatePicker field={field} />;
                        }}
                      />
                    </div>
                  )}
                </Grid>
              )}

              {!hasAddedQuoteDates && (
                <AddDateDetailsButton
                  buttonNamePosthog={ButtonNamePosthog.AddQuoteDateDetails}
                  onClick={() => setHasAddedQuoteDates(true)}
                />
              )}

              <Flex direction='col' gap='xs' className='w-full'>
                <Typography variant='h6' weight='semibold' className='mb-2'>
                  Find Carriers
                </Typography>
                <FindCarrierByGroup
                  name='carrierGroupId'
                  onGroupSelect={handleGroupSelect}
                />
              </Flex>

              <Button
                type='submit'
                disabled={
                  !selectedCarrierGroup ||
                  selectedCarrierGroup.carriers.length === 0 ||
                  loading
                }
                buttonNamePosthog={ButtonNamePosthog.DraftEmailForCarrierGroup}
                className='w-full'
              >
                {loading ? 'Processing...' : 'Draft Email for Selected Group'}
              </Button>
            </Flex>
          </form>
        </FormProvider>

        {showMessageCarrier && carrierLocations.length > 0 && (
          <FormProvider {...messageCarrierFormMethods}>
            <form
              onSubmit={messageCarrierFormMethods.handleSubmit(
                onSubmitMessageCarriers
              )}
              className='grid grid-cols-1 mx-0 w-full'
            >
              <MessageCarrierForm
                email={email}
                emailTemplates={emailTemplates}
                loading={loading}
                carrierLocations={carrierLocations}
                selectedCarriers={selectedCarriers}
                toggleSelection={toggleSelection}
                setNewAttachments={setNewAttachments}
                config={config}
                areAllCarriersSelected={areAllCarriersSelected}
                onToggleSelectAllCarriers={handleToggleSelectAllCarriers}
              />
            </form>
          </FormProvider>
        )}
      </ExtendedFormProvider>
    </Grid>
  );
}

interface LocationInputProps {
  name: string;
  label: string;
  placeholder: string;
  required?: boolean;
}

const LocationInput = ({
  name,
  label,
  placeholder,
  required = false,
}: LocationInputProps) => {
  return (
    <Flex direction='col' gap='xs' className='w-full'>
      <RHFTextInput
        name={name}
        label={label}
        placeholder={placeholder}
        required={required}
      />
    </Flex>
  );
};

const defaultDeliveryLocation: Address & { location: Maybe<string> } = {
  name: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zip: '',
  country: 'USA',
  location: null,
};
